import { CotizacionFormState } from './types';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'src/app/store';
import {
  colorMapping,
  CotizacionEstado,
} from 'src/utils/enums/CotizacionEstado';
import { aprobarCotizacion } from './apiThunk';

const initialState: CotizacionFormState = {
  // Status
  status: null,

  // Partes del form
  actions: null,
  data: null,
  productos: null,
  summary: null,

  // Extra
  previousState: null,
};

const formSlice = createSlice({
  name: 'cotizacionForm',
  initialState,
  reducers: {
    setFormStatusBorrador(state) {
      // Status
      state.status = {
        id: CotizacionEstado.Borrador,
        text: CotizacionEstado[CotizacionEstado.Borrador].toUpperCase(),
        color: colorMapping[CotizacionEstado.Borrador],
      };

      // Form items
      state.data = {
        nro: { readOnly: true },
        notas: { readOnly: false },
        validez: { readOnly: false },
        descuento: { readOnly: true },
        condiciones: { readOnly: false },
        plazoEntrega: { readOnly: false },
        monedaArsUsd: { readOnly: false },
        nroProveedorCliente: { readOnly: true },
        fechaEnviado: { readOnly: true, visible: false },
        reason: {
          readOnly: true,
          value: 'Modificaciones en borrador',
          visible: false,
        },
        clienteId: {
          readOnly: false,
          value: null,
          placeholder: 'Seleccionar cliente...',
        },
      };

      // Set actions
      state.actions = {
        agregarProductos: {
          tooltip: 'Agregar productos',
          disabled: false,
        },
        rechazar: {
          tooltip: 'Para rechazar la cotización debe estar Enviada',
          disabled: true,
          visible: false,
        },
        guardar: {
          tooltip: 'Guardar cotización',
          disabled: false,
          visible: true,
        },
        descuento: {
          tooltip: 'Agregar descuento',
          disabled: false,
          visible: true,
        },
        anular: {
          tooltip: 'Para anular la cotización debe estar Enviada',
          disabled: true,
          visible: false,
        },
        aprobar: {
          tooltip: 'Aprobar cotización',
          disabled: false,
          visible: true,
        },
        exportarPdf: {
          tooltip: 'Para generar un PDF la cotización debe estar Enviada',
          disabled: true,
          visible: false,
        },
        email: {
          tooltip: 'Enviar e-mail con la cotización',
          disabled: false,
          visible: true,
        },
        modificar: {
          tooltip: 'Modificar',
          disabled: true,
          visible: false,
        },
        revertir: {
          tooltip:
            'No se puede restaurar el estado previo de la cotización en Borrador.',
          disabled: true,
          visible: false,
        },
        verificarStock: {
          tooltip: 'Verificar stock de insumos.',
          disabled: false,
          visible: true,
        },
      };

      // Listas
      state.productos = { readOnly: false };
    },
    setFormStatusEnviada(state) {
      state.status = {
        id: CotizacionEstado.Enviada,
        text: CotizacionEstado[CotizacionEstado.Enviada].toUpperCase(),
        color: colorMapping[CotizacionEstado.Enviada],
      };

      // Form items
      state.data = {
        nro: { readOnly: true },
        validez: { readOnly: true },
        reason: { readOnly: true, value: '' },
        descuento: { readOnly: true },
        fechaEnviado: { readOnly: true },
        clienteId: {
          readOnly: true,
          placeholder: 'No se pueden seleccionar clientes.',
        },
        condiciones: { readOnly: true },
        plazoEntrega: { readOnly: true },
        monedaArsUsd: { readOnly: true },
        nroProveedorCliente: { readOnly: true },
        notas: { readOnly: true },
      };

      // Productos
      state.productos = { readOnly: true };

      // Actions
      state.actions = {
        agregarProductos: {
          tooltip: 'Agregar productos',
          disabled: true,
        },
        rechazar: {
          tooltip: 'Rechazar cotización',
          disabled: false,
          visible: true,
        },
        guardar: {
          tooltip: 'Guardar cotización',
          disabled: true,
          visible: false,
        },
        descuento: {
          tooltip: 'Agregar descuento',
          disabled: true,
          visible: false,
        },
        anular: {
          tooltip: 'Anular cotización',
          disabled: false,
          visible: true,
        },
        aprobar: {
          tooltip: 'Aprobar cotización',
          disabled: false,
          visible: true,
        },
        exportarPdf: {
          tooltip: 'Generar PDF',
          disabled: false,
          visible: true,
        },
        email: {
          tooltip: 'Enviar email',
          disabled: true,
          visible: false,
        },
        modificar: {
          tooltip: 'Habilita los campos y permite agregar productos',
          disabled: false,
          visible: true,
        },
        revertir: {
          tooltip: 'Restaurar el estado previo de la cotización.',
          disabled: true,
          visible: false,
        },
        verificarStock: {
          tooltip: 'Verificar stock de insumos.',
          disabled: false,
          visible: true,
        },
      };
    },
    setFormStatusAprobada(state) {
      state.status = {
        id: CotizacionEstado.Aprobada,
        text: CotizacionEstado[CotizacionEstado.Aprobada].toUpperCase(),
        color: colorMapping[CotizacionEstado.Aprobada],
      };

      // Form items
      state.data = {
        nro: { readOnly: true },
        validez: { readOnly: true },
        descuento: { readOnly: true },
        reason: { readOnly: true, value: '' },
        fechaEnviado: { readOnly: true },
        clienteId: {
          readOnly: true,
          placeholder: 'No se pueden seleccionar clientes.',
        },
        condiciones: { readOnly: true },
        plazoEntrega: { readOnly: true },
        monedaArsUsd: { readOnly: true },
        nroProveedorCliente: { readOnly: true },

        notas: { readOnly: true },
      };

      // Productos
      state.productos = { readOnly: true };

      // Actions
      state.actions = {
        agregarProductos: {
          tooltip: 'No se puede agregar productos en estado Aprobada',
          disabled: true,
        },
        rechazar: {
          tooltip: 'Para rechazar la cotización debe estar Enviada',
          disabled: true,
          visible: false,
        },
        guardar: {
          tooltip: 'No se puede guardar la cotización en estado Aprobada',
          disabled: true,
          visible: false,
        },
        descuento: {
          tooltip: 'No se puede agregar descuento en estado Aprobada',
          disabled: true,
          visible: false,
        },
        anular: {
          tooltip: 'Anular cotización',
          disabled: false,
          visible: true,
        },
        aprobar: {
          tooltip: 'No se puede aprobar cotización en estado Aprobada',
          disabled: true,
          visible: false,
        },
        exportarPdf: {
          tooltip: 'Generar PDF',
          disabled: false,
          visible: true,
        },
        email: {
          tooltip: 'Enviar email',
          disabled: true,
          visible: false,
        },
        modificar: {
          tooltip: 'Habilita los campos y permite agregar productos',
          disabled: false,
          visible: true,
        },
        revertir: {
          tooltip: 'Restaurar el estado previo de la cotización.',
          disabled: false,
          visible: true,
        },
        verificarStock: {
          tooltip: 'Verificar stock de insumos.',
          disabled: false,
          visible: true,
        },
      };
    },
    setFormStatusRechazada(state) {
      state.status = {
        id: CotizacionEstado.Rechazada,
        text: CotizacionEstado[CotizacionEstado.Rechazada].toUpperCase(),
        color: colorMapping[CotizacionEstado.Rechazada],
      };

      // Form items
      state.data = {
        nro: { readOnly: true },
        validez: { readOnly: true },
        descuento: { readOnly: true },
        reason: { readOnly: true, value: '' },
        fechaEnviado: { readOnly: true },

        clienteId: {
          readOnly: true,
          placeholder: 'No se pueden seleccionar clientes.',
        },
        condiciones: { readOnly: true },
        plazoEntrega: { readOnly: true },
        monedaArsUsd: { readOnly: true },
        nroProveedorCliente: { readOnly: true },

        notas: { readOnly: true },
      };

      // Productos
      state.productos = { readOnly: true };

      // Actions
      state.actions = {
        agregarProductos: {
          tooltip: 'No se puede agregar productos en estado Rechazada',
          disabled: true,
        },
        rechazar: {
          tooltip: 'Para rechazar la cotización debe estar Enviada',
          disabled: true,
          visible: false,
        },
        guardar: {
          tooltip: 'No se puede guardar la cotización en estado Rechazada',
          disabled: true,
          visible: false,
        },
        descuento: {
          tooltip: 'No se puede agregar descuento en estado Rechazada',
          disabled: true,
          visible: false,
        },
        anular: {
          tooltip: 'Anular cotización',
          disabled: false,
          visible: true,
        },
        aprobar: {
          tooltip: 'No se puede aprobar cotización en estado Rechazada',
          disabled: true,
          visible: false,
        },
        exportarPdf: {
          tooltip: 'Generar PDF',
          disabled: false,
          visible: true,
        },
        email: {
          tooltip: 'Enviar email',
          disabled: true,
          visible: false,
        },
        modificar: {
          tooltip: 'Habilita los campos y permite agregar productos',
          disabled: false,
          visible: true,
        },
        revertir: {
          tooltip: 'Restaurar el estado previo de la cotización.',
          disabled: false,
          visible: true,
        },
        verificarStock: {
          tooltip: 'Verificar stock de insumos.',
          disabled: false,
          visible: true,
        },
      };
    },
    setFormStatusAnulada(state) {
      state.status = {
        id: CotizacionEstado.Anulada,
        text: CotizacionEstado[CotizacionEstado.Anulada].toUpperCase(),
        color: colorMapping[CotizacionEstado.Anulada],
      };

      // Form items
      state.data = {
        nro: { readOnly: true },
        validez: { readOnly: true },
        descuento: { readOnly: true },
        fechaEnviado: { readOnly: true },

        reason: { readOnly: true, value: '' },
        clienteId: {
          readOnly: true,
          placeholder: 'No se pueden seleccionar clientes.',
        },
        condiciones: { readOnly: true },
        plazoEntrega: { readOnly: true },
        monedaArsUsd: { readOnly: true },
        nroProveedorCliente: { readOnly: true },

        notas: { readOnly: true },
      };

      // Productos
      state.productos = { readOnly: true };

      // Actions
      state.actions = {
        agregarProductos: {
          tooltip: 'No se puede agregar productos en estado Anulada',
          disabled: true,
        },
        rechazar: {
          tooltip: 'Para rechazar la cotización debe estar Enviada',
          disabled: true,
          visible: false,
        },
        guardar: {
          tooltip: 'No se puede guardar la cotización en estado Anulada',
          disabled: true,
          visible: false,
        },
        descuento: {
          tooltip: 'No se puede agregar descuento en estado Anulada',
          disabled: true,
          visible: false,
        },
        anular: {
          tooltip: 'Anular cotización',
          disabled: true,
          visible: false,
        },
        aprobar: {
          tooltip: 'No se puede aprobar cotización en estado Anulada',
          disabled: true,
          visible: false,
        },
        exportarPdf: {
          tooltip: 'Generar PDF',
          disabled: false,
          visible: true,
        },
        email: {
          tooltip: 'Enviar email',
          disabled: true,
          visible: false,
        },
        modificar: {
          tooltip: 'Habilita los campos y permite agregar productos',
          disabled: false,
          visible: true,
        },
        revertir: {
          tooltip: 'Restaurar el estado previo de la cotización.',
          disabled: false,
          visible: false,
        },
        verificarStock: {
          tooltip: 'Verificar stock de insumos.',
          disabled: false,
          visible: true,
        },
      };
    },
    setFormStatusVencida(state) {
      state.status = {
        id: CotizacionEstado.Vencida,
        text: CotizacionEstado[CotizacionEstado.Vencida].toUpperCase(),
        color: colorMapping[CotizacionEstado.Vencida],
      };

      // Form items
      state.data = {
        nro: { readOnly: true },
        validez: { readOnly: true },
        descuento: { readOnly: true },
        fechaEnviado: { readOnly: true },

        reason: { readOnly: true, value: '' },
        clienteId: {
          readOnly: true,
          placeholder: 'No se pueden seleccionar clientes.',
        },
        condiciones: { readOnly: true },
        plazoEntrega: { readOnly: true },
        monedaArsUsd: { readOnly: true },
        nroProveedorCliente: { readOnly: true },

        notas: { readOnly: true },
      };

      // Productos
      state.productos = { readOnly: true };

      // Actions
      state.actions = {
        agregarProductos: {
          tooltip: 'No se puede agregar productos en estado Vencida',
          disabled: true,
        },
        rechazar: {
          tooltip: 'Para rechazar la cotización debe estar Enviada',
          disabled: true,
          visible: false,
        },
        guardar: {
          tooltip: 'No se puede guardar la cotización en estado Vencida',
          disabled: true,
          visible: false,
        },
        descuento: {
          tooltip: 'No se puede agregar descuento en estado Vencida',
          disabled: true,
          visible: false,
        },
        anular: {
          tooltip: 'Anular cotización',
          disabled: true,
          visible: false,
        },
        aprobar: {
          tooltip: 'No se puede aprobar cotización en estado Vencida',
          disabled: true,
          visible: false,
        },
        exportarPdf: {
          tooltip: 'Generar PDF',
          disabled: false,
          visible: true,
        },
        email: {
          tooltip: 'Enviar email',
          disabled: true,
          visible: false,
        },
        modificar: {
          tooltip: 'Habilita los campos y permite agregar productos',
          disabled: false,
          visible: true,
        },
        revertir: {
          tooltip: 'Restaurar el estado previo de la cotización.',
          disabled: false,
          visible: true,
        },
        verificarStock: {
          tooltip: 'Verificar stock de insumos.',
          disabled: false,
          visible: true,
        },
      };
    },
    setModifyingProducts(state) {
      // Guardar estado anterior.
      state.previousState = {
        status: state.status,
        actions: state.actions,
      };
      state.actions = {
        agregarProductos: {
          tooltip:
            'Por favor, termine de modificar productos para agregar más.',
          disabled: true,
        },
        rechazar: {
          tooltip:
            'Por favor, termine de modificar productos para rechazar cotización.',
          disabled: true,
          visible: state.actions!.rechazar.visible,
        },
        email: {
          tooltip:
            'Por favor, termine de modificar productos para enviar email.',
          disabled: true,
          visible: state.actions!.email.visible,
        },
        exportarPdf: {
          tooltip:
            'Por favor, termine de modificar productos para generar PDF.',
          disabled: true,
          visible: state.actions!.exportarPdf.visible,
        },
        aprobar: {
          tooltip: 'Por favor, termine de modificar productos para aprobar.',
          disabled: true,
          visible: state.actions!.aprobar.visible,
        },
        guardar: {
          tooltip: 'Por favor, termine de modificar productos para guardar.',
          disabled: true,
          visible: true,
        },
        anular: {
          tooltip: 'Por favor, termine de modificar productos para anular.',
          disabled: true,
          visible: state.actions!.anular.visible,
        },
        descuento: {
          tooltip:
            'Por favor, termine de modificar productos para agregar descuento.',
          disabled: true,
          visible: true,
        },
        modificar: {
          tooltip:
            'Por favor, termine de modificar productos para realizar cambios.',
          disabled: true,
          visible: false,
        },
        revertir: {
          tooltip:
            'Por favor, termine de modificar productos para revertir estado.',
          disabled: true,
          visible: state.actions!.revertir.visible,
        },
        verificarStock: {
          tooltip: 'Verificar stock de insumos.',
          disabled: true,
          visible: true,
        },
      };
    },
    setModifyingProductsFinished(state) {
      // Restaurar estado anterior
      state.status = state.previousState!.status;
      state.actions = state.previousState!.actions;
      state.previousState = null;
    },
    setModifying(state, action: PayloadAction<any>) {
      // Guardar estado anterior
      state.previousState = {
        status: state.status,
        // Form items
        data: {
          nro: { readOnly: true, value: action.payload.formData.nro },
          notas: { readOnly: false, value: action.payload.formData.notas },
          validez: { readOnly: true, value: action.payload.formData.validez },
          descuento: {
            readOnly: true,
            value: action.payload.formData.descuento,
          },
          condiciones: {
            readOnly: true,
            value: action.payload.formData.condiciones,
          },
          plazoEntrega: {
            readOnly: true,
            value: action.payload.formData.plazoEntrega,
          },
          monedaArsUsd: {
            readOnly: true,
            value: action.payload.formData.monedaArsUsd,
          },
          nroProveedorCliente: {
            readOnly: true,
            value: action.payload.formData.nroProveedorCliente,
          },
          clienteId: {
            readOnly: true,
            placeholder: 'Seleccionar cliente...',
            value: action.payload.formData.clienteId,
          },
        },
        // Summary items
        summary: {
          totalUSD: {
            readOnly: true,
            value: action.payload.summaryData.totalUSD,
          },
          totalARS: {
            readOnly: true,
            value: action.payload.summaryData.totalARS,
          },
          pesoTotal: {
            readOnly: true,
            value: action.payload.summaryData.pesoTotal,
          },
          cantidadTotal: {
            readOnly: true,
            value: action.payload.summaryData.cantidadTotal,
          },
          totalDescuentoARS: {
            readOnly: true,
            value: action.payload.summaryData.totalDescuentoARS,
          },
          totalDescuentoUSD: {
            readOnly: true,
            value: action.payload.summaryData.totalDescuentoUSD,
          },
        },
        // Listas
        productos: { readOnly: true, value: action.payload.productsData },

        // Acciones
        actions: state.actions,
      };

      // Form items
      state.data = {
        nro: { readOnly: true },
        notas: { readOnly: false },
        reason: { readOnly: false, value: 'A completar' },
        validez: { readOnly: false },
        fechaEnviado: { readOnly: true },

        descuento: { readOnly: true },
        condiciones: { readOnly: false },
        plazoEntrega: { readOnly: false },
        monedaArsUsd: { readOnly: false },
        nroProveedorCliente: { readOnly: true },
        clienteId: {
          readOnly: false,
          placeholder: 'Seleccionar cliente...',
        },
      };

      // Listas
      state.productos = { readOnly: false };

      // Acciones
      state.actions = {
        modificar: {
          visible: false,
          disabled: true,
          tooltip: 'Modificar cotización',
        },
        rechazar: {
          tooltip: 'Rechazar cotización',
          disabled: true,
          visible: false,
        },
        guardar: {
          disabled: false,
          tooltip: 'Guardar cambios',
          visible: true,
        },
        agregarProductos: {
          disabled: false,
          tooltip: 'Agregar productos',
        },
        aprobar: {
          disabled: true,
          tooltip: 'Aprobar cotización',
          visible: false,
        },
        email: {
          disabled: true,
          tooltip: 'Enviar email',
          visible: false,
        },
        anular: {
          disabled: true,
          tooltip: 'Anular cotización',
          visible: false,
        },
        revertir: {
          tooltip: 'Restaurar el estado previo de la cotización.',
          disabled: true,
          visible: false,
        },
        descuento: {
          disabled: false,
          tooltip: 'Agregar descuento',
          visible: true,
        },
        exportarPdf: { ...state.actions!.exportarPdf },
        verificarStock: {
          tooltip: 'Verificar stock de insumos.',
          disabled: true,
          visible: true,
        },
      };
    },
    resetForm(state) {
      state.data = null;
      state.status = null;
      state.summary = null;
      state.actions = null;
      state.productos = null;
      state.previousState = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(aprobarCotizacion.fulfilled, (state, action) => {
      setFormStatusAprobada();
    });
  },
});

// Selectors

export const selectRechazar = (state: RootState) =>
  state.cotizacion.form.actions?.rechazar;
export const selectRevertir = (state: RootState) =>
  state.cotizacion.form.actions?.revertir;
export const selectAprobar = (state: RootState) =>
  state.cotizacion.form.actions?.aprobar;
export const selectAnular = (state: RootState) =>
  state.cotizacion.form.actions?.anular;
export const selectDescuento = (state: RootState) =>
  state.cotizacion.form.actions?.descuento;
export const selectExportarPdf = (state: RootState) =>
  state.cotizacion.form.actions?.exportarPdf;
export const selectModificar = (state: RootState) =>
  state.cotizacion.form.actions?.modificar;
export const selectGuardar = (state: RootState) =>
  state.cotizacion.form.actions?.guardar;
export const selectAgregarProductos = (state: RootState) =>
  state.cotizacion.form.actions?.agregarProductos;
export const selectEmail = (state: RootState) =>
  state.cotizacion.form.actions?.email;
export const selectVerificarStock = (state: RootState) =>
  state.cotizacion.form.actions?.verificarStock;
export const selectFormStatus = (state: RootState) =>
  state.cotizacion.form.status;
export const selectFormProductos = (state: RootState) =>
  state.cotizacion.form.productos;
export const selectFormSummary = (state: RootState) =>
  state.cotizacion.form.summary;
export const selectFormDataReason = (state: RootState) =>
  state.cotizacion.form.data?.reason;

// Export actions

export const {
  resetForm,
  setModifying,
  setModifyingProducts,
  setFormStatusEnviada,
  setFormStatusVencida,
  setFormStatusAprobada,
  setFormStatusBorrador,
  setFormStatusAnulada,
  setFormStatusRechazada,
  setModifyingProductsFinished,
} = formSlice.actions;

export default formSlice.reducer;
